import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import React, { useCallback, useMemo, useRef, useState } from 'react';

import {
  FILTERS,
  FOLDER_UPLOAD_CATEGORY,
  FilterType,
  classifyFile,
  uploadActionSx,
} from './attachmentFilters';
import { AttachmentRow } from './AttachmentRow';
import { AttachmentsPanelProps } from './AttachmentsPanel.props';
import { getUploadRejection } from './attachmentUploadGuards';
import { getUploadErrorMessage } from './getUploadErrorMessage';
import { ImageViewer, ViewerImage } from './ImageViewer';
import { Folders, UploadedFile } from '../../../@types/uploaded-files.type';
import { uploadFiles } from '../../../api/uploadedFiles';
import { useGetUploadedFiles } from '../../../queries/uploadedfiles-query';
import { isSafeUrl } from '../../../utils/common';
import { toast } from '../../../utils/toast';
import { Panel } from '../../atoms/Panel';

export const AttachmentsPanel: React.FC<AttachmentsPanelProps> = ({
  entityId,
  folder = Folders.ServiceAttachments,
  uploadCategory,
  accept,
  maxFileSize,
  onArchive,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [filter, setFilter] = useState<FilterType>('all');
  const [uploadError, setUploadError] = useState('');
  const [viewerIndex, setViewerIndex] = useState<number | null>(null);
  const [archivingId, setArchivingId] = useState<number | null>(null);
  const filesQuery = useGetUploadedFiles(entityId, folder);
  const attachments = filesQuery.data ?? [];

  const handleArchive = useCallback(
    async (id: number) => {
      if (!onArchive) return;
      setArchivingId(id);
      try {
        await onArchive(id);
        filesQuery.refetch();
      } catch {
        // Without this the rejection escaped the click handler: the row simply
        // stopped spinning and the file was still there, with nothing said.
        toast.error('Could not remove the attachment', {
          description: 'The file is still there. Try again in a moment.',
        });
      } finally {
        setArchivingId(null);
      }
    },
    [onArchive, filesQuery]
  );

  const imageAttachments = useMemo<(UploadedFile & ViewerImage)[]>(
    () =>
      attachments
        .filter((f) => f.mimeType?.startsWith('image/'))
        .map((f) => ({
          ...f,
          url: f.url,
          fileName: f.fileName ?? f.key?.split('/').pop() ?? 'Image',
        })),
    [attachments]
  );

  const filtered = useMemo(() => {
    if (filter === 'all') return attachments;
    return attachments.filter((f) => classifyFile(f.mimeType) === filter);
  }, [attachments, filter]);

  const counts = useMemo(() => {
    const c: Record<FilterType, number> = {
      all: attachments.length,
      images: 0,
      pdfs: 0,
      documents: 0,
      other: 0,
    };
    for (const f of attachments) {
      c[classifyFile(f.mimeType)]++;
    }
    return c;
  }, [attachments]);

  const handleUpload = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      if (!files?.length || !entityId) return;

      setUploadError('');

      const rejection = getUploadRejection(files, { accept, maxFileSize });
      if (rejection) {
        setUploadError(rejection);
        if (fileInputRef.current) fileInputRef.current.value = '';
        return;
      }

      const formData = new FormData();
      for (let i = 0; i < files.length; i++) {
        formData.append('fileList', files[i]);
      }

      try {
        await uploadFiles(
          entityId,
          formData,
          folder,
          uploadCategory ?? FOLDER_UPLOAD_CATEGORY[folder]
        );
        filesQuery.refetch();
      } catch (err) {
        setUploadError(getUploadErrorMessage(err));
      }

      if (fileInputRef.current) fileInputRef.current.value = '';
    },
    [entityId, folder, uploadCategory, filesQuery, accept, maxFileSize]
  );

  return (
    <Panel title="Attachments">
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept={accept?.length ? accept.join(',') : undefined}
        onChange={handleUpload}
        style={{ display: 'none' }}
        aria-label="Upload attachments"
      />

      {/* Filter chips */}
      {attachments.length > 0 && (
        <Box sx={{ display: 'flex', gap: 0.5, mb: 1, flexWrap: 'wrap' }}>
          {FILTERS.map((f) => {
            const count = counts[f.key];
            if (f.key !== 'all' && count === 0) return null;
            const active = filter === f.key;
            return (
              <Chip
                key={f.key}
                label={`${f.label}${f.key !== 'all' ? ` (${count})` : ''}`}
                size="small"
                onClick={() => setFilter(f.key)}
                sx={{
                  height: 22,
                  fontSize: '10px',
                  fontWeight: active ? 600 : 400,
                  bgcolor: active ? 'v4.accent' : 'v4.tableEven',
                  color: active ? 'common.white' : 'text.secondary',
                  cursor: 'pointer',
                  '&:hover': { opacity: 0.85 },
                }}
              />
            );
          })}
        </Box>
      )}

      {filtered.length > 0 ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
          {filtered.map((file, idx) => (
            <AttachmentRow
              key={file.id ?? idx}
              file={file}
              index={idx}
              archiving={archivingId === file.id}
              onArchive={
                onArchive && file.id !== undefined
                  ? () => handleArchive(file.id as number)
                  : undefined
              }
              onOpen={() => {
                if (file.mimeType?.startsWith('image/')) {
                  const i = imageAttachments.findIndex((img) => img.id === file.id);
                  setViewerIndex(i >= 0 ? i : 0);
                } else if (isSafeUrl(file.url)) {
                  // Guarded: the href comes from the API, and an unexpected
                  // scheme (javascript:, data:) must not be opened.
                  window.open(file.url, '_blank', 'noopener,noreferrer');
                }
              }}
            />
          ))}
        </Box>
      ) : (
        // Compact rather than a centred void: an empty panel shouldn't reserve
        // more room than a populated one.
        <Typography sx={{ fontSize: '13px', color: 'text.secondary', py: 0.5 }}>
          {filesQuery.isLoading ? (
            <CircularProgress size={16} sx={{ color: 'v4.accent' }} />
          ) : attachments.length > 0 ? (
            'No matching files'
          ) : (
            'No attachments yet.'
          )}
        </Typography>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
        <Button
          size="small"
          variant="contained"
          onClick={() => fileInputRef.current?.click()}
          sx={uploadActionSx}
        >
          Upload
        </Button>
      </Box>

      {uploadError && (
        <Typography sx={{ fontSize: '11px', color: 'error.main', mt: 0.5 }}>
          {uploadError}
        </Typography>
      )}

      {viewerIndex !== null && imageAttachments.length > 0 && (
        <ImageViewer
          images={imageAttachments}
          initialIndex={viewerIndex}
          onClose={() => setViewerIndex(null)}
        />
      )}
    </Panel>
  );
};
